import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PublicClientApplication, AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalConfig, getLoginRequest, getSilentRequest } from './msalConfig';
import { useAppDispatch } from '../app/hooks';
import { getAzureAdLoginInfo, AzureAdTokenInfo } from '../actions/loginInfo';
import { getConfig, getConfigSync } from '../helpers/configHelper';

// Interfaz para el contexto de Azure Auth
interface IAzureAuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userInfo: {
    username: string;
    name: string;
    idToken: string;
    accessToken: string;
  } | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: () => Promise<string | null>;
  processAuth: (authResult: any) => Promise<void>;
}

// Crear el contexto para Azure Auth
export const AzureAuthContext = createContext<IAzureAuthContext>({
  isAuthenticated: false,
  isLoading: false,
  error: null,
  userInfo: null,
  login: async () => {},
  logout: async () => {},
  acquireToken: async () => null,
  processAuth: async () => {},
});

// Hook para usar el contexto
export const useAzureAuth = () => useContext(AzureAuthContext);

// Proveedor de autenticación con Azure AD (versión simplificada)
export const AzureAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{
    username: string;
    name: string;
    idToken: string;
    accessToken: string;
  } | null>(null);
  
  const dispatch = useAppDispatch();

  // Crear instancia de MSAL
  const [msalInstance] = useState<PublicClientApplication>(() => {
    const config = getConfigSync();
    const useAdfAuth = config?.USE_ADF_AUTH === true;
    if (useAdfAuth) {
      try {
        return new PublicClientApplication(getMsalConfig());
      } catch (error) {
        console.error('Error creando instancia MSAL:', error);
        return null as any;
      }
    }
    return null as any;
  });

  // Inicializar MSAL
  useEffect(() => {
    const config = getConfigSync();
    const useAdfAuth = config?.USE_ADF_AUTH === true;
    if (!useAdfAuth || !msalInstance) return;

    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        
        // Manejar la redirección si viene de una autenticación
        const authResult = await msalInstance.handleRedirectPromise();
        if (authResult) {
          processAuth(authResult);
        } else {
          // Comprobar si el usuario ya ha iniciado sesión
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
            setIsAuthenticated(true);
            setUserInfo({
              username: accounts[0].username || '',
              name: accounts[0].name || '',
              idToken: '',
              accessToken: ''
            });
          }
        }
      } catch (error) {
        console.error("Error durante la inicialización:", error);
        setError('Error inicializando Azure AD');
      }
    };
    
    initializeMsal();
  }, [msalInstance]);

  // Función para iniciar sesión
  const login = async () => {
    const config = getConfigSync();
    const useAdfAuth = config?.USE_ADF_AUTH === true;
    
    if (!useAdfAuth) {
      console.log('Azure AD login is disabled. Set REACT_APP_USE_ADF_AUTH=true to enable.');
      setError('La autenticación de Azure AD no está habilitada');
      return;
    }

    if (!msalInstance) {
      console.error('MSAL instance not available');
      setError('Azure AD no se inicializó correctamente');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await msalInstance.loginRedirect(getLoginRequest());
    } catch (error) {
      console.error('Error durante el login de Azure AD:', error);
      setError('Error durante el login de Azure AD');
      setIsLoading(false);
    }
  };

  // Función para cerrar sesión
  const logout = async () => {
    const config = getConfigSync();
    const useAdfAuth = config?.USE_ADF_AUTH === true;
    
    if (useAdfAuth && msalInstance) {
      try {
        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: config?.AZURE_POST_LOGOUT_REDIRECT_URI
        });
      } catch (error) {
        console.error('Error durante el logout de Azure AD:', error);
      }
    }
    
    // Limpiar estado local
    setIsAuthenticated(false);
    setUserInfo(null);
    localStorage.removeItem('used_adf_auth');
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('logged');
  };

  // Función para obtener token de acceso
  const acquireToken = async (): Promise<string | null> => {
    const config = getConfigSync();
    const useAdfAuth = config?.USE_ADF_AUTH === true;
    
    if (!useAdfAuth || !msalInstance) {
      return null;
    }

    try {
      const account = msalInstance.getActiveAccount();
      if (!account) {
        console.log('No active account found');
        return null;
      }

      const result = await msalInstance.loginPopup(getLoginRequest());
      return result.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          // Si el token silencioso falla, intentar con redirección
          await msalInstance.acquireTokenRedirect(getLoginRequest());
          return null; // El token se obtendrá después de la redirección
        } catch (redirectError) {
          console.error('Error obteniendo token por redirección:', redirectError);
          return null;
        }
      }
      console.error('Error obteniendo token silenciosamente:', error);
      return null;
    }
  };

  // Procesar el flujo de autenticación
  const processAuth = useCallback(async (authResult: AuthenticationResult) => {
    const config = getConfigSync();
    const useAdfAuth = config?.USE_ADF_AUTH === true;
    
    if (!useAdfAuth) {
      console.log('processAuth called but ADF is disabled');
      return;
    }

    try {
      // Extraer tokens y claims de la respuesta de autenticación
      const idToken = authResult.idToken;
      const accessToken = authResult.accessToken;
      const account = authResult.account;
      
      // Intentar obtener el email de múltiples fuentes
      let email = account?.username || '';
      
      // Si no hay email en username, intentar extraerlo de los claims del ID token
      if (!email && idToken) {
        try {
          // Decodificar el payload del JWT (sin verificar la firma, solo para extraer claims)
          const base64Payload = idToken.split('.')[1];
          const payload = JSON.parse(atob(base64Payload));
          email = payload.email || payload.preferred_username || payload.upn || '';
          console.log('Email extraído de claims del token:', email);
          console.log('Claims disponibles:', Object.keys(payload));
        } catch (decodeError) {
          console.error('Error decodificando ID token:', decodeError);
        }
      }
      
      // DEBUG: Mostrar información del token y email
      console.log('=== Azure AD Authentication Result ===');
      console.log('Email:', email);
      console.log('Account username:', account?.username);
      console.log('Account name:', account?.name);
      console.log('ID Token (first 100 chars):', idToken ? idToken.substring(0, 100) + '...' : 'NULL');
      console.log('Access Token (first 100 chars):', accessToken ? accessToken.substring(0, 100) + '...' : 'NULL');
      console.log('Full account object:', account);
      console.log('=====================================');
      
      setIsAuthenticated(true);
      setUserInfo({
        username: account?.username || '',
        name: account?.name || '',
        idToken,
        accessToken
      });

      console.log(config)
      
      // Obtener el servidor de destino
      // Intentar: localStorage -> configSync -> await getConfig()
      let serverUrl = localStorage.getItem('server_url') || config?.SERVER_URL || '';
      if (!serverUrl) {
        try {
          const loaded = await getConfig();
          serverUrl = loaded.SERVER_URL;
          if (serverUrl) {
            // Persistir para siguientes usos
            localStorage.setItem('server_url', serverUrl);
          }
        } catch (e) {
          // Se gestiona abajo si sigue vacío
        }
      }
      if (!serverUrl) {
        console.error('No se encontró URL del servidor');
        setError('No se encontró URL del servidor');
        setIsLoading(false);
        return;
      }
      
      try {
        // Crear la información del token para enviar al backend
        const tokenInfo: AzureAdTokenInfo = {
          idToken,
          accessToken,
          email,
          serverUrl
        };
        
        // Usar la acción de Redux para gestionar la autenticación con el backend
        dispatch(getAzureAdLoginInfo(tokenInfo));
        // La redirección ahora se maneja dentro de getAzureAdLoginInfo
      } catch (error) {
        console.error('Error intercambiando token con backend:', error);
        setError('Error validando token con el servidor');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error procesando autenticación:', error);
      setIsAuthenticated(false);
      setError('Error procesando la autenticación');
      setIsLoading(false);
    }
  }, [dispatch]);

  // Valor del contexto
  const contextValue: IAzureAuthContext = {
    isAuthenticated,
    isLoading,
    error,
    userInfo,
    login,
    logout,
    acquireToken,
    processAuth,
  };

  return (
    <AzureAuthContext.Provider value={contextValue}>
      {children}
    </AzureAuthContext.Provider>
  );
};

export default AzureAuthProvider;
